"use client";

import Link from "next/link";
import { ClipboardIcon } from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/components/ui/use-toast";

import { formURL, emailSupport, phoneSupport } from "@/constants";

import type { MouseEvent } from "react";

interface DoubtsClientProps {
  hasPaidPlan?: boolean;
}

export const DoubtsClient = ({ hasPaidPlan }: DoubtsClientProps) => {
  const { toast } = useToast();

  const copy = (e: MouseEvent, value: string) => {
    e.preventDefault();
    e.stopPropagation();

    navigator.clipboard
      .writeText(value)
      .then(() => {
        toast({
          variant: "default",
          title: "Copiado!",
        });
      })
      .catch((error) => {
        const errorMessage =
          error instanceof Error ? error.message : "Erro ao copiar.";

        toast({
          variant: "destructive",
          title: errorMessage,
        });
      });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="fixed bottom-5 right-5 h-[42px] w-[42px] z-10 rounded-full bg-white shadow-xl border border-gray-300 focus:outline-none print:hidden">
          <p className="font-medium text-primary">?</p>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-56"
        side="top"
        sideOffset={10}
        collisionPadding={{ right: 20 }}
      >
        <DropdownMenuLabel>Ajuda</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link
              prefetch={false}
              href="/perguntas-frequentes"
              target="_blank"
              className="w-full cursor-default"
            >
              Perguntas frequentes
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem>
            <Link
              prefetch={false}
              href="/ferramentas/glossario"
              className="w-full cursor-default"
            >
              Glossário
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="flex justify-between" asChild>
            <a
              href={formURL}
              target="_blank"
              rel="nofollow"
              className="w-full cursor-default"
            >
              Enviar feedback
            </a>
          </DropdownMenuItem>
          <DropdownMenuItem className="flex justify-between" asChild>
            <a
              href={`mailto:${emailSupport}?subject=Pro Leilão - Dúvida`}
              target="_blank"
              rel="nofollow"
              className="w-full cursor-default"
            >
              Suporte via Email
              <Button
                variant="ghost"
                size="icon"
                className="size-4"
                onClick={(e) => copy(e, emailSupport)}
              >
                <ClipboardIcon className="size-4" />
              </Button>
            </a>
          </DropdownMenuItem>
          {hasPaidPlan && (
            <DropdownMenuItem className="flex justify-between" asChild>
              <a
                href={`https://wa.me/${phoneSupport}?text=Olá,%20estou%20entrando%20em%20contato%20atráves%20do%20Pro%20Leilão...`}
                target="_blank"
                rel="nofollow"
                className="w-full cursor-default"
              >
                Suporte via WhatsApp
              </a>
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
